import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { App, Modal } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import { getPurchasedAddons } from '../../../graphql/custom-queries'
import { useAppSelector } from '../../../store/hooks'
import { roleAdmin, selectAuthUserSlice } from '../../../store/auth-user-slice'
import { isTrial, selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { selectDateFormatSlice } from '../../../store/date-format-slice'
import { notificationStore } from '../../../context/notificationsContext/store'
import { getExtendedAddons } from '../common/addons'
import { ProductService } from '@vacationtracker/shared/data/stripe/product-service'
import { track } from '../../../services/analytics/analytics'
import IntlMessages from '../../../util/IntlMessages'

import BlackoutPeriodView from '../../../components/blackout-period-view'
import SeniorityEntitlementView from '../../../components/seniority-entitlement-view'
import EntitlementByRoleView from '../../../components/entitlement-by-role-view'
import ProbationPeriodView from '../../../components/probation-period-view'
import OpenApiView from '../../../components/open-api-view'
import CircularProgress from '../../../components/circular-progress'
import VisibilityAddonView from '../../../components/visibility-addon'
import MaximumUsersAwayView from '../../../components/maximum-users-away-view'

import { IPageParams } from '../../../types/data'
import { IGetPurchasedAddonsData } from '../../../types/custom-queries'
import { FrontendUrls } from '../../../types/urls'
import { IAddon } from '@vacationtracker/shared/types/addons'
import { IAddonsData } from '@vacationtracker/shared/types/automations'
import { ProductName } from '@vacationtracker/shared/data/stripe/prices'
import LeaveDurationLimitView from '../../../components/leave-duration-limit-view'
import { selectFeatureFlagsSlice } from '../../../store/feature-flags-slice'
import LeaveRequestNoticePeriodView from '../../../components/leave-request-notice-period-view'
import AdpAddonView from '../../../components/adp/addon-view'

const AddonViewPage = ({ match }: IPageParams): React.ReactElement => {
  const history = useHistory()
  const { notification } = App.useApp()

  const { dateFormat } = useAppSelector(selectDateFormatSlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const { formatMessage } = useIntl()

  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const amIAdmin = useSelector(roleAdmin)
  const isTrialSubscription = useSelector(isTrial)

  const selectAddonFn = () => {
    const addonsEventTypes = getExtendedAddons(formatMessage, featureFlags)
    if (match?.params?.addonType) {
      const addon = addonsEventTypes.find(addon => {
        return addon.url === match.params.addonType.replace(/_/g, '-')
      })
      return addon as IAddonsData
    }
    return undefined
  }

  const selectAddonTypeFn = () => {
    const addon = selectAddonFn()
    if (addon) {
      return addon?.type as string
    }
    return null
  }

  const [selectAddonType, setSelectAddonType] = useState<string | null>(selectAddonTypeFn)

  const [selectAddon] = useState<IAddonsData | undefined>(selectAddonFn)
  const [correlationId, setCorrelationId] = useState<string | null>(null)
  const [subscribedAddons, setSubscribedAddons] = useState<IAddon[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [visibleSubscribeFreeAddon, setVisibleSubscribeFreeAddon] = useState<boolean>(false)
  const [selectedAddonType, setSelectedAddonType] = useState<string | null>()
  const [isLoadingButtonAddon, setIsLoadingButtonAddon] = useState<boolean>(false)

  const env = process.env.REACT_APP_ENVIRONMENT === 'production' ? 'prod' : 'test'
  const productService = new ProductService(env)
  const addonsPrice = productService.getPriceUnitByProductName(ProductName.ADDONS)

  const abortControllerRef = useRef<AbortController>()

  const [getPurchasedAddonsQuery] = useManualQuery<IGetPurchasedAddonsData>(getPurchasedAddons)

  useEffect(() => {
    abortControllerRef.current = new AbortController()
    if (isLoading) {
      fetchData()
    }
    if (correlationId && !actionNotifications.includes(correlationId)) {
      setIsLoadingButtonAddon(false)
      setCorrelationId(null)
      fetchData()
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [actionNotifications])

  // Refresh the addon page when feature flags are loaded
  // TODO: we need a load state for feature flags so we can handle these situations better
  useEffect(() => {
    setSelectAddonType(selectAddonTypeFn)
  }, [featureFlags])

  const fetchData = async () => {
    const abortController = abortControllerRef.current
    const response = await getPurchasedAddonsQuery({
      fetchOptionsOverrides: {
        signal: abortController?.signal,
      },
    })
    if (!response.data || response.error) throw response.error

    const subscribedAddons = [
      ...(response.data?.getPurchasedAddons?.addons || []),
      ...(response.data?.getPurchasedAddons?.automations || []),
      ...(response.data?.getPurchasedAddons?.integrations || []),
    ]
    setSubscribedAddons(subscribedAddons as IAddon[])

    setIsLoading(false)
  }

  const deleteAddon = async (automationId: string, name: string, automationType: string, options?: { deleteSeniorityEntitlementAddedDays?: boolean }) => {
    let response
    try {
      response = await Api.post('/core/event', {
        eventType: `${automationType}_DELETED`,
        eventGroup: 'AUTOMATION',
        automationId,
        ...options,
      })

      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'automations.deleteInProgress' }, { automationName: name }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setCorrelationId(response.correlationId as string)
      setActionNotifications([ ...actionNotifications, response.correlationId ])
    } catch (error) {
      let message = formatMessage({ id: 'app.deleteFailed' })
      let description: string | undefined = undefined
      if (error.response?.data?.error) {
        description = formatMessage({ id: error.response.data.error })
      } else if (error.message === 'Request failed with status code 400') {
        message = formatMessage({ id: 'error.automations.insufficientPrivileges.title' })
        description = formatMessage({ id: 'error.automations.insufficientPrivileges.description' })
      } else {
        description = response.correlationId ? formatMessage({ id: 'app.correlationIdError' }, { correlationId: response.correlationId }) : JSON.stringify(error)
      }
      showErrorNotification(error, message, description)
    }
  }

  const handleSubscribe = async (automationType: string, isActive) => {
    if (!amIAdmin) return
    try {
      setIsLoadingButtonAddon(true)
      const response = await Api.post('/core/event', {
        eventType: 'ADDONS_UPDATED',
        eventGroup: 'BILLING',
        type: automationType,
        isActive,
      })

      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: isActive ? 'automations.activateInProgress' : 'automations.deactivateInPgoress' }),
        description: formatMessage({ id: isActive ? 'automations.activateInProgressDescription' : 'automations.deactivateInPgoressDescription' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setCorrelationId(response.correlationId as string)
      setActionNotifications([ ...actionNotifications, response.correlationId ])
      if (!isActive) {
        track('ADDON_UNSUBSCRIBED', { addonName: automationType })
      } else {
        if (subscribedAddons.find(addon => addon.type === automationType)?.status === 'CANCELED') {
          track('ADDON_RESUBSCRIBED', { addonName: automationType })
        } else {
          track('ADDON_SUBSCRIBED', { addonName: automationType })
        }
      }

      if (!isActive && isTrialSubscription) {
        history.push(`${FrontendUrls.addons}`)
      }
    } catch (error) {
      showErrorNotification(error)
      setIsLoadingButtonAddon(false)
    }
  }

  const showErrorNotification = (error, message?: string, description?: string) => {
    const errorDescription = description ? description : error.response?.data?.message ? error.response?.data.message : error.message ? error.message : JSON.stringify(error)
    notification.error({
      message: message ? message : formatMessage({ id: 'error.generic' }),
      description: errorDescription,
      duration: 0,
    })
  }

  return (
    <>
      {isLoading ?
        <CircularProgress /> :
        <>
          {selectAddonType === 'BLACKOUT_PERIOD' &&
            <BlackoutPeriodView
              formatMessage={formatMessage}
              amIAdmin={amIAdmin}
              deleteAutomation={(id: string, name: string, automationType: string) => {
                deleteAddon(id, name, automationType)
              }}
              authUserId={authUser.id}
              dateFormat={dateFormat}
              handleSubscribe={(automationType: string, isActive: boolean) => {
                handleSubscribe(automationType, isActive)
              }}
              status={subscribedAddons.find(addon => addon.type === 'BLACKOUT_PERIOD')?.status}
              addonDetails={selectAddon}
              currentPlan={authCompany?.plan}
            />
          }
          {selectAddonType === 'SENIORITY_ENTITLEMENT' &&
            <SeniorityEntitlementView
              formatMessage={formatMessage}
              amIAdmin={amIAdmin}
              deleteAutomation={(id: string, name: string, automationType: string, options?: { deleteSeniorityEntitlementAddedDays?: boolean }) => {
                deleteAddon(id, name, automationType, options)
              }}
              authUserId={authUser.id}
              dateFormat={dateFormat}
              handleSubscribe={(automationType: string, isActive: boolean) => {
                handleSubscribe(automationType, isActive)
              }}
              status={subscribedAddons.find(addon => addon.type === 'SENIORITY_ENTITLEMENT')?.status}
              addonDetails={selectAddon}
              currentPlan={authCompany?.plan}
            />
          }
          {selectAddonType === 'ENTITLEMENT_BY_ROLE' &&
            <EntitlementByRoleView
              formatMessage={formatMessage}
              amIAdmin={amIAdmin}
              deleteAutomation={(id: string, name: string, automationType: string) => {
                deleteAddon(id, name, automationType)
              }}
              authUserId={authUser.id}
              dateFormat={dateFormat}
              handleSubscribe={(automationType: string, isActive: boolean) => {
                handleSubscribe(automationType, isActive)
              }}
              status={subscribedAddons.find(addon => addon.type === 'ENTITLEMENT_BY_ROLE')?.status}
              addonDetails={selectAddon}
              currentPlan={authCompany?.plan}
            />
          }
          {selectAddonType === 'VISIBILITY' &&
            <VisibilityAddonView
              formatMessage={formatMessage}
              amIAdmin={amIAdmin}
              authUserId={authUser.id}
              dateFormat={dateFormat}
              handleSubscribe={(automationType: string, isActive: boolean) => {
                handleSubscribe(automationType, isActive)
              }}
              status={subscribedAddons.find(addon => addon.type === 'VISIBILITY')?.status}
              backToAddons={() => history.push(FrontendUrls.addons)}
              addonDetails={selectAddon}
              currentPlan={authCompany?.plan}
            />
          }
          {selectAddonType === 'PROBATION_PERIOD' &&
            <ProbationPeriodView
              formatMessage={formatMessage}
              amIAdmin={amIAdmin}
              deleteAutomation={(id: string, name: string, automationType: string) => {
                deleteAddon(id, name, automationType)
              }}
              authUserId={authUser.id}
              dateFormat={dateFormat}
              handleSubscribe={(automationType: string, isActive: boolean) => {
                handleSubscribe(automationType, isActive)
              }}
              status={subscribedAddons.find(addon => addon.type === 'PROBATION_PERIOD')?.status}
              addonDetails={selectAddon}
              currentPlan={authCompany?.plan}
            />
          }
          {selectAddonType === 'MAXIMUM_USERS_AWAY' &&
            <MaximumUsersAwayView
              formatMessage={formatMessage}
              amIAdmin={amIAdmin}
              authUserId={authUser.id}
              dateFormat={dateFormat}
              handleSubscribe={(automationType: string, isActive: boolean) => {
                handleSubscribe(automationType, isActive)
              }}
              status={subscribedAddons.find(addon => addon.type === 'MAXIMUM_USERS_AWAY')?.status}
              deleteAutomation={(id: string, name: string, automationType: string) => {
                deleteAddon(id, name, automationType)
              }}
              addonDetails={selectAddon}
              currentPlan={authCompany?.plan}
            />
          }
          {selectAddonType === 'LEAVE_DURATION_LIMIT' &&
            <LeaveDurationLimitView
              formatMessage={formatMessage}
              amIAdmin={amIAdmin}
              deleteAutomation={(id: string, name: string, automationType: string) => {
                deleteAddon(id, name, automationType)
              }}
              authUserId={authUser.id}
              dateFormat={dateFormat}
              handleSubscribe={(automationType: string, isActive: boolean) => {
                handleSubscribe(automationType, isActive)
              }}
              status={subscribedAddons.find(addon => addon.type === 'LEAVE_DURATION_LIMIT')?.status}
              addonDetails={selectAddon}
              currentPlan={authCompany?.plan}
            />
          }
          {selectAddonType === 'LEAVE_REQUEST_NOTICE_PERIOD' &&
            <LeaveRequestNoticePeriodView
              formatMessage={formatMessage}
              amIAdmin={amIAdmin}
              deleteAutomation={(id: string, name: string, automationType: string) => {
                deleteAddon(id, name, automationType)
              }}
              authUserId={authUser.id}
              dateFormat={dateFormat}
              handleSubscribe={(automationType: string, isActive: boolean) => {
                handleSubscribe(automationType, isActive)
              }}
              status={subscribedAddons.find(addon => addon.type === 'LEAVE_REQUEST_NOTICE_PERIOD')?.status}
              addonDetails={selectAddon}
              currentPlan={authCompany?.plan}
            />
          }
          {selectAddonType === 'OPEN_API' &&
            <OpenApiView
              formatMessage={formatMessage}
              amIAdmin={amIAdmin}
              deleteAutomation={(id: string, name: string, automationType: string) => {
                deleteAddon(id, name, automationType)
              }}
              authUserId={authUser.id}
              dateFormat={dateFormat}
              handleSubscribe={(automationType: string, isActive: boolean) => {
                handleSubscribe(automationType, isActive)
              }}
              handleBuySubscriptionModal={(parent: string, automationType: string) => {
                setSelectedAddonType(automationType)
                setVisibleSubscribeFreeAddon(true)
              }}
              status={subscribedAddons.find(addon => addon.type === 'OPEN_API')?.status}
              allowViewMode={true}
              addonDetails={selectAddon}
              currentPlan={authCompany?.plan}
            />
          }
          {selectAddonType === 'ADP' && <AdpAddonView/>}
        </>
      }

      <Modal
        title={amIAdmin ? <IntlMessages id="automations.subscripteToAddons" /> : <IntlMessages id="automations.onlyAdminCanEnableAddons" />}
        open={visibleSubscribeFreeAddon}
        onCancel={() => {
          setVisibleSubscribeFreeAddon(false)
          setSelectedAddonType(null)
        }}
        onOk={() => {
          if (amIAdmin) {
            handleSubscribe(selectedAddonType as string, true)
            setVisibleSubscribeFreeAddon(false)
          } else {
            setVisibleSubscribeFreeAddon(false)
          }
        }}
        okText={amIAdmin ? <IntlMessages id="app.confirm" /> : <IntlMessages id="app.ok" />}
        cancelText={<IntlMessages id="app.close" />}
        confirmLoading={isLoadingButtonAddon}
      >
        <>
          {amIAdmin && <IntlMessages id="automations.addFreeAddonsDescirptionForAdmin" />}
          {!amIAdmin && <IntlMessages id="automations.addFreeAddonsDescirptionForApprover" />}
        </>
      </Modal>
    </>
  )
}

export default AddonViewPage
